/* eslint-disable no-prototype-builtins */

import { isLeft } from "fp-ts/Either"
import { PathReporter } from "io-ts/PathReporter"

import { BASIC_FORM } from "../../../../components/submitApplication/single/formGenerator/formClassKeys"
import {
    internalAppError,
    invalidApiDataError,
} from "../../../../errors/recognisedErrors"
import {
    ApplicationSpec,
    IApplicationProperties,
    IApplicationSpec,
    ICombineProperties,
    IGetSchemaMapper,
    IPossibleApplicationForms,
    isBasicFormClass,
    isSingleArrayFormClass,
    isRecognisedFormClass,
    IApplicationForms,
    IApplicationStepIndexMap,
    IFormTitlesIndexMap,
    IFixedInputs,
} from "../../types/singleApplication/getSchemaTypes"

type FormData = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any
}

const ORDER_KW = "order"
const reviewKey = "Review and Submit"
const reviewSpec = {
    [reviewKey]: {
        title: reviewKey,
        subtitle: "Review and Submit",
    },
}

export const getSchemaMapper = {
    /**
     * Maps `getschema` response into UI data model
     */
    map: (response: unknown): IGetSchemaMapper => {
        // Validate the API response
        const rawApplicationSpec = getSchemaMapper.validateApplicationSpec(
            response,
            "Could not validate data from getSchema endpoint"
        )

        // Extract form steps and application spec
        const {
            applicationStepIndexMap,
            formTitlesIndexMap,
            applicationForms,
            fixedInputs,
        } = getSchemaMapper.deriveSupportingInfoFromRawSpec(rawApplicationSpec)

        return {
            rawApplicationSpec,
            applicationStepIndexMap,
            formTitlesIndexMap,
            applicationForms,
            fixedInputs,
        }
    },

    /** Validates the proposed merge is valid */
    validateCombineProperties: (
        propsCanBeMergedKeys: string[],
        allCombines: string[]
    ) => {
        const canAllCombinePropsBeMerged = allCombines.every((key) =>
            propsCanBeMergedKeys.includes(key)
        )

        if (!canAllCombinePropsBeMerged) {
            const detail = `There was an error with the get schema API response.
            Some properties are listed combineProperties that are not suitable
            for merging`
            throw invalidApiDataError(detail)
        }

        const uniqueCombines = Array.from(new Set(allCombines))
        if (allCombines.length !== uniqueCombines.length) {
            const detail = `There was an error with the get schema API response.
            Duplicate references were found in the combineProperties object.`
            throw invalidApiDataError(detail)
        }
    },

    /** Merges combined properties, generates new schema and returns it */
    generateMergedSchema: (
        properties: IApplicationProperties,
        combineProperties: Record<string, ICombineProperties>
    ) =>
        Object.entries(combineProperties).reduce((acc, [key, value]) => {
            // Create a set to store unique keys
            const keysSet = new Set<string>()

            // Check for duplicate keys before merging properties
            value.combines.forEach((property) => {
                const targetProperty = properties[property]
                if (isBasicFormClass(targetProperty)) {
                    Object.keys(targetProperty.properties).forEach(
                        (propKey) => {
                            if (keysSet.has(propKey)) {
                                const detail = `There was an error with the get schema
                                                API response. The forms being merged have the
                                                same property key: ${propKey}`
                                throw invalidApiDataError(detail)
                            }
                            keysSet.add(propKey)
                        }
                    )
                }
            })

            // Create a new property with the combined title and subtitle
            const newProperty = {
                title: value.title,
                subtitle: value.subtitle,
                formClass: BASIC_FORM,
                order: value.order,
                properties: value.combines.reduce((propAcc, property) => {
                    const targetProperty = properties[property]
                    if (isBasicFormClass(targetProperty)) {
                        return {
                            ...propAcc,
                            ...targetProperty.properties,
                        }
                    }
                    throw invalidApiDataError(`Failed to parse schema. A merged property
                                                is not of the correct type.`)
                }, {}),
            }

            // Remove the original properties from the properties object. Es-lint
            // disable as the properties object has already been re-created
            value.combines.forEach((property) => {
                // eslint-disable-next-line no-param-reassign
                delete properties[property]
            })

            return {
                ...acc,
                [key]: newProperty,
            }
        }, {}),

    /** Merges properties if valid */
    mergeProperties: (applicationSpec: IApplicationSpec): IApplicationSpec => {
        const { combineProperties, properties: originalProperties } =
            applicationSpec

        // Create a copy of the original properties
        const properties = { ...originalProperties }

        // Extract the properties which can be merged
        // These are properties where the formClass is BASIC
        const propsCanBeMerged = Object.entries(properties).filter(
            ([, value]) => isBasicFormClass(value)
        )

        const allCombines = Object.values(combineProperties).flatMap(
            (value) => value.combines
        )

        // Validate combine properties
        getSchemaMapper.validateCombineProperties(
            propsCanBeMerged.map(([key]) => key),
            allCombines
        )

        // Merge properties
        const mergedProperties = getSchemaMapper.generateMergedSchema(
            properties,
            combineProperties
        )

        const updatedSpec = {
            ...applicationSpec,
            properties: {
                ...properties,
                ...mergedProperties,
            },
        }

        return getSchemaMapper.validateApplicationSpec(
            updatedSpec,
            "Something went wrong whilst merging inputs in mapping layer"
        )
    },

    /**
     * Validates the application spec using TS-IO `IApplicationSpec` type.
     */
    validateApplicationSpec: (
        response: unknown,
        errorMsg: string
    ): IApplicationSpec => {
        const decoded = ApplicationSpec.decode(response)
        if (isLeft(decoded)) {
            const detail = `${errorMsg}: ${PathReporter.report(decoded).join(
                "\n"
            )}`
            throw invalidApiDataError(detail)
        }
        const applicationSpec: IApplicationSpec = decoded.right
        return applicationSpec
    },

    /**
     * Called on submit. Parses the form data into the format required by the API. This requires
     * splitting any merged properties into their constituent parts. This is called within
     * a try-catch block in the `useSubmitApplication` hook.
     */
    splitMergedProperties: (
        formData: FormData,
        existingSchema: IApplicationSpec
    ): FormData =>
        Object.entries(existingSchema.combineProperties).reduce(
            (splitData, [key, value]) => {
                // Create a deep copy of splitData
                const newData = JSON.parse(JSON.stringify(splitData))
                if (Object.prototype.hasOwnProperty.call(newData, key)) {
                    const mergedData = newData[key]

                    value.combines.forEach((property) => {
                        if (
                            Object.prototype.hasOwnProperty.call(
                                existingSchema.properties,
                                property
                            )
                        ) {
                            const existingSchemaForm =
                                existingSchema.properties[property]

                            // only form specs can be merged. If this is not a form spec, throw an error
                            // as something has gone wrong
                            if (!isBasicFormClass(existingSchemaForm)) {
                                throw internalAppError(
                                    "The create application form entered an invalid state. The splitMergedProperties mapper function was asked to unmerge a property which is not a form spec (ie isn't a property that should be merged)"
                                )
                            }

                            const propertyKeys = Object.keys(
                                existingSchemaForm.properties
                            )
                            const newPropertyData = propertyKeys.reduce(
                                (acc, propertyKey) => {
                                    if (
                                        Object.prototype.hasOwnProperty.call(
                                            mergedData,
                                            propertyKey
                                        )
                                    ) {
                                        acc[propertyKey] =
                                            mergedData[propertyKey]
                                        delete mergedData[propertyKey]
                                    }
                                    return acc
                                },
                                {} as any
                            )

                            if (Object.keys(newPropertyData).length > 0) {
                                newData[property] = newPropertyData
                            }
                        }
                    })

                    if (Object.keys(mergedData).length === 0) {
                        delete newData[key]
                    }
                }
                return newData
            },
            { ...formData }
        ),
    /** Sorts properties in application spec based on order */
    sortProperties: (appSpec: IApplicationSpec): IApplicationSpec => {
        const propertiesArray = Object.entries(appSpec.properties)

        propertiesArray.sort(([, valueA], [, valueB]) => {
            const orderA =
                ORDER_KW in valueA && valueA.order !== undefined
                    ? valueA.order
                    : -1
            const orderB =
                ORDER_KW in valueB && valueB.order !== undefined
                    ? valueB.order
                    : -1

            return orderA - orderB
        })

        const sortedProperties = Object.fromEntries(propertiesArray)

        return { ...appSpec, properties: sortedProperties }
    },

    /** Creates a map of step name to title and subtitle */
    createTitleIndexMap: (
        formSteps: string[],
        applicationSpec: IApplicationSpec
    ): IFormTitlesIndexMap =>
        formSteps.reduce<{
            [key: string]: { title: string; subtitle: string }
        }>(
            (obj, key) => ({
                ...obj,
                [key]: {
                    title: applicationSpec.properties[key].title,
                    subtitle: applicationSpec.properties[key].subtitle,
                },
            }),
            reviewSpec
        ),

    /** Creates form steps and application spec */
    createFormStepsAndAppSpec: (
        rawApplicationSpec: IApplicationSpec
    ): {
        formSteps: string[]
        applicationSpec: IApplicationSpec
    } => {
        const applicationSpecUnsorted =
            getSchemaMapper.mergeProperties(rawApplicationSpec)

        const applicationSpec = getSchemaMapper.sortProperties(
            applicationSpecUnsorted
        )
        return {
            formSteps: Object.keys(applicationSpec.properties),
            applicationSpec,
        }
    },

    /** Generates application step index map */
    createApplicationStepIndexMap: (
        formSteps: string[]
    ): IApplicationStepIndexMap => {
        // Add the "Review and Submit" step
        const applicationSteps = [...formSteps, reviewKey]

        // Create a map of step index to step name
        return applicationSteps.reduce<{
            [key: number]: string
        }>((obj, key, index) => ({ ...obj, [index]: key }), {})
    },

    createApplicationForms: (
        formSteps: string[],
        applicationSpec: IApplicationSpec
    ): IApplicationForms =>
        formSteps.reduce<{
            [key: string]: IPossibleApplicationForms
        }>((obj, key) => {
            const formSpec = applicationSpec.properties[key]
            if (
                isRecognisedFormClass(formSpec) ||
                isSingleArrayFormClass(formSpec)
            ) {
                return {
                    ...obj,
                    [key]: formSpec,
                }
            }
            return {
                ...obj,
                [key]: formSpec.properties,
            }
        }, {}),

    deriveSupportingInfoFromRawSpec: (
        rawApplicationSpec: IApplicationSpec
    ): {
        applicationStepIndexMap: IApplicationStepIndexMap
        formTitlesIndexMap: IFormTitlesIndexMap
        applicationForms: IApplicationForms
        fixedInputs: IFixedInputs
        formSteps: string[]
        applicationSpec: IApplicationSpec
    } => {
        const { formSteps, applicationSpec } =
            getSchemaMapper.createFormStepsAndAppSpec(rawApplicationSpec)

        // Create a map of step index to step name
        const applicationStepIndexMap =
            getSchemaMapper.createApplicationStepIndexMap(formSteps)

        // Create a map of step name to title and subtitle
        const formTitlesIndexMap = getSchemaMapper.createTitleIndexMap(
            formSteps,
            applicationSpec
        )
        // Generate schemas for each application forms
        const applicationForms = getSchemaMapper.createApplicationForms(
            formSteps,
            applicationSpec
        )

        // Extract fixed inputs
        const { fixedInputs } = applicationSpec
        return {
            formSteps,
            applicationSpec,
            applicationStepIndexMap,
            formTitlesIndexMap,
            applicationForms,
            fixedInputs,
        }
    },
}
